import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Notification } from 'iconsax-react';

// ui/CountUp은 useInView 기반이라 3D transform(preserve-3d) 컨테이너 안에서
// IntersectionObserver가 발화하지 않는다 — 마운트 즉시 재생하는 로컬 카운터 사용.
const MountCountUp: React.FC<{ to: number; duration?: number; delay?: number }> = ({ to, duration = 1.6, delay = 0.4 }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (v) => Math.round(v).toLocaleString('ko-KR'));
    useEffect(() => {
        const controls = animate(count, to, { duration, delay, ease: 'easeOut' });
        return controls.stop;
    }, [to, duration, delay, count]);
    return <motion.span>{rounded}</motion.span>;
};

// 코드 기반 하이퍼프레임 — MP4 영상이 아니라 DOM/CSS/framer-motion 라이브 렌더.
// 방문자 분석 리포트가 실제로 동작하는 장면(방문 알림 도착 + 리포트 갱신)을
// 무한 루프로 재생한다. 크롤 가능한 실제 DOM 텍스트, 0바이트 미디어.
// reduced-motion 사용자는 정적 리포트만 표시.

const BARS = [30, 44, 38, 62, 50, 72, 88, 66, 54, 80, 60, 70];

const ROWS = [
    { handle: '@m***.studio', note: '오늘 또 다녀감', visits: 14, score: 94 },
    { handle: '@j***_archive', note: '스토리 본 뒤 확인', visits: 9, score: 78 },
] as const;

const BANNERS = [
    { title: '방금 프로필 방문이 감지되었습니다', body: '최근 1시간 조회 3회' },
    { title: '8개월 전 게시물에 새 좋아요가 추가되었습니다', body: '여기까지 스크롤한 사람이 있어요' },
    { title: '프로필 조회가 평소보다 3.2배 빠르게 늘고 있어요', body: '지금 진행 중인 활동이에요' },
] as const;

const VisitorReportHyperframe: React.FC = () => {
    const reduce = useReducedMotion();
    const [banner, setBanner] = useState<{ id: number; msg: (typeof BANNERS)[number] } | null>(null);

    useEffect(() => {
        if (reduce) return;
        let idx = 0;
        let hideTimer: number;
        const showNext = () => {
            setBanner({ id: idx, msg: BANNERS[idx % BANNERS.length] });
            idx += 1;
            hideTimer = window.setTimeout(() => setBanner(null), 3000);
        };
        const t0 = window.setTimeout(showNext, 1600);
        const timer = window.setInterval(showNext, 5600);
        return () => {
            window.clearTimeout(t0);
            window.clearTimeout(hideTimer);
            window.clearInterval(timer);
        };
    }, [reduce]);

    return (
        <div className="relative flex justify-center">
            {/* 배경 글로우는 2026-11-03 첫 화면 새 디자인에서 뺌 — 대신 HeroSection의 흰 스튜디오 조명·바닥 그림자 */}

            {/* width는 인라인으로 고정 — Tailwind arbitrary 클래스가 전역 스타일에 밀리는 이슈 회피 */}
            <div style={{ perspective: 1500, width: 'min(72vw, 300px)' }}>
                <motion.div
                    className="relative w-full"
                    style={{ aspectRatio: '360 / 680', transformStyle: 'preserve-3d' }}
                    initial={{ rotateY: -14, rotateX: 4 }}
                    animate={reduce ? { rotateY: -8 } : { rotateY: [-14, 12, -14], y: [0, -14, 0] }}
                    transition={reduce ? undefined : { duration: 11, repeat: Infinity, ease: 'easeInOut' }}
                >
                    {/* 디바이스 프레임 */}
                    <div
                        className="absolute inset-0 rounded-[54px] bg-[#0b0d10]"
                        style={{ boxShadow: '0 0 0 2px #20262e, 0 0 0 12px #0a0c0f, 0 60px 120px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.04)' }}
                    />
                    <div className="absolute inset-0 rounded-[54px]" style={{ boxShadow: 'inset 2px 0 14px rgba(49,130,246,0.18), inset -2px 0 14px rgba(49,130,246,0.10)' }} />

                    {/* 스크린 */}
                    <div className="absolute inset-[14px] overflow-hidden rounded-[42px] border border-[#161b21] bg-[#0A0B0D]">
                        <div className="absolute left-1/2 top-[14px] z-[5] h-[30px] w-[104px] -translate-x-1/2 rounded-[18px] bg-black" />
                        <div className="absolute left-6 top-4 z-[4] text-[13px] font-bold text-[#cfd6dd]">21:47</div>

                        {/* 실시간 알림 배너 — 주기적으로 상단에서 드롭 */}
                        <AnimatePresence>
                            {banner && (
                                <motion.div
                                    key={banner.id}
                                    initial={{ opacity: 0, y: -46, scale: 0.96 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -30, scale: 0.97 }}
                                    transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                                    className="absolute inset-x-[12px] top-[50px] z-[6] rounded-[14px] border border-white/[0.08] bg-[#1a1f27]/95 p-2.5 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="mt-[1px] flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[6px] bg-[#3182F6]">
                                            <Notification size={12} variant="Bold" className="text-white" style={{ color: '#ffffff' }} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center justify-between gap-1">
                                                <span className="text-[9px] font-bold text-white/90">소셜데이터랩</span>
                                                <span className="shrink-0 text-[8.5px] text-white/45">지금</span>
                                            </div>
                                            <p className="truncate text-[10px] font-semibold leading-snug text-white/90">{banner.msg.title}</p>
                                            <p className="truncate text-[9px] leading-snug text-white/55">{banner.msg.body}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* 리포트 본문 */}
                        <div className="absolute inset-x-[18px] bottom-[18px] top-[62px]">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className="h-3 w-3 rounded bg-[#3182F6]" />
                                    <div className="text-[15px] font-extrabold text-white">SocialDataLab</div>
                                </div>
                                <div className="flex items-center gap-1.5 rounded-full border border-[#00D87E]/25 bg-[#00D87E]/10 px-2.5 py-1 text-[11px] font-bold text-[#00D87E]">
                                    <motion.span
                                        className="h-1.5 w-1.5 rounded-full bg-[#00D87E]"
                                        animate={reduce ? undefined : { opacity: [1, 0.25, 1] }}
                                        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                                    />
                                    LIVE
                                </div>
                            </div>

                            <div className="mt-4 rounded-2xl border border-[#1B2229] bg-[#111416] p-4">
                                <div className="text-[11px] uppercase tracking-wide text-[#8A8A8A]">지난 24시간 내 프로필 방문</div>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className="text-[46px] font-black leading-none text-white">
                                        <MountCountUp to={27} duration={1.6} />
                                    </span>
                                    <span className="text-[30px] font-black text-white">회</span>
                                </div>
                                <div className="text-[12px] font-semibold text-[#A9DFFF]">어제보다 3.2배 많아요</div>
                                <div className="mt-3.5 flex h-[54px] items-end gap-[5px]">
                                    {BARS.map((h, i) => (
                                        <motion.span
                                            key={i}
                                            className="w-[7px] rounded-t-[2px] bg-[#3182F6]"
                                            style={{ height: Math.max(10, h * 0.55), opacity: 0.85, transformOrigin: 'bottom' }}
                                            initial={{ scaleY: 0 }}
                                            animate={{ scaleY: 1 }}
                                            transition={{ duration: 0.5, delay: 0.5 + i * 0.03, ease: 'easeOut' }}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-2.5 text-[13px] font-bold text-white">가장 자주 돌아온 방문</div>
                                {ROWS.map((row, i) => (
                                    <motion.div
                                        key={row.handle}
                                        className="mb-2.5"
                                        initial={{ opacity: 0, x: 14 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.45, delay: 0.7 + i * 0.14, ease: 'easeOut' }}
                                    >
                                        <div className="mb-1.5 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#222] text-[10px] font-extrabold text-white">{i + 1}</span>
                                                <span className="text-[12px] font-bold text-white">{row.handle}</span>
                                                <span className="text-[10px] text-[#7DC8FF]">{row.note}</span>
                                            </div>
                                            <span className="text-[11px] text-[#A8A8A8]">{row.visits}회</span>
                                        </div>
                                        <div className="h-1.5 overflow-hidden rounded-full bg-[#242424]">
                                            <motion.div
                                                className="h-full rounded-full bg-[#00D87E]"
                                                style={{ transformOrigin: 'left' }}
                                                initial={{ scaleX: 0 }}
                                                animate={{ scaleX: row.score / 100 }}
                                                transition={{ duration: 0.7, delay: 0.9 + i * 0.14, ease: 'easeOut' }}
                                            />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-3.5 grid grid-cols-2 gap-2.5">
                                <div className="rounded-xl border border-[#1B1D21] bg-[#0D0E10] p-2.5">
                                    <div className="text-[10px] text-[#8A8A8A]">가장 활발한 시간</div>
                                    <div className="mt-0.5 text-[15px] font-extrabold text-white">저녁 7–10시</div>
                                </div>
                                <div className="rounded-xl border border-[#1B1D21] bg-[#0D0E10] p-2.5">
                                    <div className="text-[10px] text-[#8A8A8A]">반복 확인</div>
                                    <div className="mt-0.5 text-[15px] font-extrabold text-[#3182F6]">평균 3.2회</div>
                                </div>
                            </div>

                            {/* 다음 감지 대기 펄스 — 리포트가 지금도 살아있다는 신호 */}
                            <div className="mt-4 flex items-center justify-center gap-2">
                                <span className="relative flex h-2 w-2">
                                    <motion.span
                                        className="absolute inline-flex h-full w-full rounded-full bg-[#3182F6]"
                                        animate={reduce ? undefined : { scale: [1, 2.4], opacity: [0.6, 0] }}
                                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
                                    />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#3182F6]" />
                                </span>
                                <motion.span
                                    className="text-[11px] font-medium text-[#8A8A8A]"
                                    animate={reduce ? undefined : { opacity: [0.55, 1, 0.55] }}
                                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                                >
                                    다음 분석 준비 중…
                                </motion.span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default VisitorReportHyperframe;
